import MatchEnum from "~src/enums/MatchEnum";
import MatchTennisEnum from "~src/enums/tennis/MatchEnum";
import MatchEnumVolleyball from "~src/enums/volleyball/MatchEnum";

import type {
  IMatch,
  IMatchLive,
  ICompetitions,
  ITeam,
  ILeague,
  IStage,
  IRound,
  IItemString,
  ILineups,
  IIncident,
  IPlayer,
  IStatistics,
  IStatsJsonData,
  IShootingPoints,
  IShootingPointResponseData,
  IPlayerDataShooting,
  ITimeline,
  ITournament,
  ICountry,
  IH2h,
  IVS,
  IScoreStatistics,
  IScoreDynamic,
  ITournamentGroup,
  IScores,
} from "~src/interfaces/tennis";
import StatsEnum from "~src/enums/tennis/StatsEnum";

export const parseMatchData = (matchDataString: string): Array<IMatch> => {
  if (!matchDataString) {
    return [];
  }

  const matchDataArray = matchDataString.split("!!");
  const matches = matchDataArray.map((matchString) => {
    const matchDetails = matchString.split("^");

    const agg_score = matchDetails[35] ? matchDetails[35].split("-") : [];

    return {
      id: matchDetails[0],
      match_time: matchDetails[1] as unknown as number,
      half_start_time: matchDetails[2],
      competition_id: matchDetails[3],
      season_id: matchDetails[4],
      status_id: parseInt(matchDetails[5]),
      home_team_id: matchDetails[6],
      home_team: {
        id: matchDetails[6],
        name: matchDetails[7],
      } as unknown as ITeam,
      home_scores: [
        parseInt(matchDetails[8]),
        parseInt(matchDetails[9]),
        parseInt(matchDetails[10]),
        parseInt(matchDetails[11]),
        parseInt(matchDetails[12]),
        parseInt(matchDetails[13]),
        parseInt(matchDetails[14]),
      ],
      home_position: matchDetails[15],
      away_team_id: matchDetails[16],
      away_team: {
        id: matchDetails[16],
        name: matchDetails[17],
      } as unknown as ITeam,
      away_scores: [
        parseInt(matchDetails[18]),
        parseInt(matchDetails[19]),
        parseInt(matchDetails[20]),
        parseInt(matchDetails[21]),
        parseInt(matchDetails[22]),
        parseInt(matchDetails[23]),
        parseInt(matchDetails[24]),
      ],
      away_position: matchDetails[25],
      round: {
        stage_id: matchDetails[26],
        group_num: matchDetails[27],
        round_num: matchDetails[28],
      } as unknown as IRound,
      stage: {
        id: matchDetails[29],
        name: matchDetails[30],
        mode: matchDetails[31] as unknown as number,
        group_count: matchDetails[32] as unknown as number,
        round_count: matchDetails[33] as unknown as number,
        order: matchDetails[34] as unknown as number,
      } as unknown as IStage,
      agg_score: agg_score as unknown as Array<number>,
      leg: matchDetails[36] ?? "",
    } as unknown as IMatch;
  });

  return matches;
};

export const parseCompetitionData = (
  competitionDataString: string
): Array<ICompetitions> => {
  if (!competitionDataString) {
    return [];
  }

  const competitionDataArray = competitionDataString.split("!!");
  const competitions = competitionDataArray.map((competitionString) => {
    const competitionDetails = competitionString.split("^");

    return {
      id: competitionDetails[0],
      name: competitionDetails[1],
      short_name: competitionDetails[2],
      type: competitionDetails[3] ? parseInt(competitionDetails[3]) : 0,
      popularity: competitionDetails[4] ? parseInt(competitionDetails[4]) : 0,
      primary_color: competitionDetails[5],
      secondary_color: competitionDetails[6],
      cur_season_id: competitionDetails[8],
    } as unknown as ICompetitions;
  });

  return competitions;
};

export const getScoresTeam = (scores: Record<string, [number, number]>) => {
  const _home: number[] = [];
  const _away: number[] = [];

  Object.values(scores).forEach(([homeScore, awayScore]) => {
    _home.push(homeScore);
    _away.push(awayScore);
  });

  return {
    home: _home,
    away: _away,
  };
};

export const parseMatchH2hData = (item: any, homeTeamId: string) => {
  const listStatusPlaying = MatchTennisEnum.LIST_STATUS_PLAYING;

  const statusEnd = MatchTennisEnum.STATUS_END;

  const status_id = item.status_id;
  const Kick_off_time = item.match_time;
  const home_team_id = item.home_team_id;
  const away_team_id = item.away_team_id;

  const home_team = item.home_team;
  const away_team = item.away_team;

  const home_scores = item.scores ? getScoresTeam(item.scores).home : [];
  const away_scores = item.scores ? getScoresTeam(item.scores).away : [];

  let matchTimePlayer = null;
  if (listStatusPlaying.includes(status_id)) {
    matchTimePlayer = getTimeDetailLive(status_id, Kick_off_time);
  } else if (status_id === statusEnd) {
    const status = determineMatchOutcome(item, homeTeamId);
    let statusTxt = "";
    if (status === "win") {
      statusTxt = "Thắng";
    } else if (status === "draw") {
      statusTxt = "Hòa";
    } else {
      statusTxt = "Thua";
    }

    matchTimePlayer = {
      text: statusTxt,
      status_h2h: status,
    };
  }

  const _competition_id =
    item?.competition_id && item.competition_id.trim() !== ""
      ? item.competition_id
      : item?.tournament_id && item.tournament_id.trim() !== ""
      ? item.tournament_id
      : item?.unique_tournament_id && item.unique_tournament_id.trim() !== ""
      ? item.unique_tournament_id
      : "";

  const __data = {
    id: item.id,
    competition_id: _competition_id,
    status_id,
    match_time: item.match_time,
    Kick_off_time,
    home_scores,
    away_scores,
    home_team_id,
    away_team_id,
    match_time_player: matchTimePlayer,
    scores: item?.scores ?? {},
    away_team,
    home_team,
  };

  return __data;
};

export const getTotalScore = (match: IMatch) => {
  const home_scores = match.home_scores ?? [];
  const away_scores = match.away_scores ?? [];

  let homeScore = 0;
  let awayScore = 0;
  let homeWin = 0;
  let awayWin = 0;
  home_scores.forEach((score: number, index: number) => {
    homeScore += score;
    awayScore += away_scores[index] ? away_scores[index] : away_scores[index];
    if (score > away_scores[index]) {
      homeWin++;
    } else {
      awayWin++;
    }
  });

  return {
    homeScore,
    awayScore,
    homeWin,
    awayWin,
  };
};

export const determineMatchOutcome = (matchData: any, homeTeamId: string) => {
  const home_team_id = matchData.home_team_id;
  const away_team_id = matchData.away_team_id;
  const home_scores = matchData.scores
    ? getScoresTeam(matchData.scores).home
    : [];
  const away_scores = matchData.scores
    ? getScoresTeam(matchData.scores).away
    : [];

  const totalHomeGoals = home_scores.reduce(
    (partialSum: number, a: number) => partialSum + a,
    0
  );

  const totalAwayGoals = away_scores.reduce(
    (partialSum: number, a: number) => partialSum + a,
    0
  );

  let homeWinTotal = 0;
  let awayWinTotal = 0;
  home_scores.forEach((score: number, index: number) => {
    if (score > away_scores[index]) {
      homeWinTotal++;
    } else {
      awayWinTotal++;
    }
  });

  if (matchData.scores && matchData.scores?.ft) {
    homeWinTotal = matchData.scores?.ft[0] ?? 0;
    awayWinTotal = matchData.scores?.ft[1] ?? 0;
  }

  const isActualHomeTeam = home_team_id === homeTeamId;
  // Xác định trạng thái trận đấu dựa trên tổng số bàn thắng
  if (homeWinTotal > awayWinTotal) {
    return isActualHomeTeam ? "win" : "lose"; // Kiểm tra đội nhà có phải là đội mình quan tâm không
  } else if (homeWinTotal === awayWinTotal) {
    return "draw"; // Hòa
  } else {
    return isActualHomeTeam ? "lose" : "win"; // Đảo ngược kết quả nếu đội mình quan tâm không phải là đội nhà
  }
};

export const determineMatchOutcome1 = (match: IMatch, homeTeamId: string) => {
  const totalHomeGoals = match.home_scores ? match.home_scores[0] : 0;
  const totalAwayGoals = match.away_scores ? match.away_scores[0] : 0;

  const isActualHomeTeam = match.home_team?.id === homeTeamId;
  // Xác định trạng thái trận đấu dựa trên tổng số bàn thắng
  if (totalHomeGoals > totalAwayGoals) {
    return isActualHomeTeam ? "win" : "lose"; // Kiểm tra đội nhà có phải là đội mình quan tâm không
  } else if (totalHomeGoals === totalAwayGoals) {
    return "draw"; // Hòa
  } else {
    return isActualHomeTeam ? "lose" : "win"; // Đảo ngược kết quả nếu đội mình quan tâm không phải là đội nhà
  }
};

export const determineMatchResult = (match: IMatch, teamId: string) => {
  if (match?.home_team?.id === teamId) {
    if (
      match.home_scores?.[0] !== undefined &&
      match.away_scores?.[0] !== undefined
    ) {
      if (match.home_scores[0] > match.away_scores[0]) return "w";
      if (match.home_scores[0] < match.away_scores[0]) return "l";
    }
  } else if (match?.away_team?.id === teamId) {
    if (
      match.home_scores?.[0] !== undefined &&
      match.away_scores?.[0] !== undefined
    ) {
      if (match.home_scores[0] < match.away_scores[0]) return "w";
      if (match.home_scores[0] > match.away_scores[0]) return "l";
    }
  }

  return "d";
};

export const formatRateColor = (e: number) => {
  var colors = [
    "#FF203C",
    "#FF6600",
    "#FFAE0F",
    "#C1CC01",
    "#5CB400",
    "#0E8604",
  ];

  if (e < 6) return colors[0];
  if (e >= 6 && e <= 6.49) return colors[1];
  if (e >= 6.5 && e <= 6.99) return colors[2];
  if (e >= 7 && e <= 7.99) return colors[3];
  if (e >= 8 && e <= 8.99) return colors[4];
  if (e >= 9) return colors[5];

  return void 0;
};

const getCurrentDateTimestamp = () => {
  const currentDateTimestamp = Math.floor(Date.now() / 1000);

  return currentDateTimestamp;
};

const timestampToMinutesSeconds = (timestamp: number) => {
  const minutes = Math.floor(timestamp / 60);
  const seconds = timestamp % 60;

  return { minutes, seconds };
};

export const getTimeDetailLive = (
  statusId: number,
  startTimestamp: any = null,
  sportType: string = 'tennis'
) => {
  let stautsText = MatchTennisEnum.MATCH_STATUS_TEXT as {
    [key: string]: any;
  };

  if(sportType === 'volleyball') {
    stautsText = MatchEnumVolleyball.MATCH_STATUS_TEXT as {
      [key: string]: any;
    };
  }

  const statusTxt = stautsText[statusId] || "";
  let timePlayerProcess = 0;

  if (!startTimestamp) {
    return {
      text: statusTxt,
      process: timePlayerProcess,
    };
  }

  const currentDate: number = getCurrentDateTimestamp();
  const { minutes } = timestampToMinutesSeconds(12 * 60 - startTimestamp);
  const minutesApp: number = minutes + 1;

  // % thời gian đá
  // const timeProcess = {
  //   2: minutesApp > 45 ? 50 : ((minutesApp / 120) * 100),
  //   4: minutesApp + 45 > 100 ? 90 : (((45 + minutesApp) / 120) * 100),
  //   5:
  //     minutesApp + 90 > 105
  //       ? (105 / 120) * 100
  //       : (((90 + minutesApp) / 120) * 100),
  //   6:
  //     minutesApp + 105 > 120 ? 100 : (((105 + minutesApp) / 120) * 100),
  // } as {[key: number]: any;};

  const statusTimeTexts = {
    2: minutesApp > 12 ? "12+'" : minutesApp + "'",
    4: minutesApp > 12 ? "12+'" : minutesApp + "'",
    6: minutesApp > 12 ? "12+'" : minutesApp + "'",
    8: minutesApp > 12 ? "12+'" : minutesApp + "'",
  } as { [key: number]: any };

  const timeText = statusTimeTexts[statusId]
    ? `: ${statusTimeTexts[statusId]}`
    : "";

  // timePlayerProcess = timeProcess[statusId] ? timeProcess[statusId] : 0;

  return {
    text: `${statusTxt}${timeText}`,
    process: timePlayerProcess,
  };
};

export const getTimeUtc = (time: any) => {
  const utcDateString = time.toUTCString();

  return new Date(utcDateString).getTime() / 1000;
};

export const unReverseId = (id: number) => {
  if (!id) {
    return id;
  }

  const sum = id
    .toString()
    .split("")
    .reduce((acc, digit) => acc + parseInt(digit, 10), 0);
  const firstNumber = sum < 10 ? 1 : parseInt(sum.toString()[0], 10);
  const lastNumber = sum < 10 ? sum : parseInt(sum.toString().slice(-1), 10);

  return parseInt(
    firstNumber + id.toString().split("").reverse().join("") + lastNumber
  );
};

export const addMatchDetailLive = async (
  matchs: Array<IMatch>,
  detailLive: Array<IMatchLive>,
  teams: ITeam[],
  sportType: string = 'tennis'
) => {
  const datas = [];
  let listStatusPlaying = MatchTennisEnum.LIST_STATUS_PLAYING;
  if (sportType === 'volleyball') {
    listStatusPlaying = MatchEnumVolleyball.LIST_STATUS_PLAYING;
  }

  const statusEnd = MatchTennisEnum.STATUS_END;

  const teamsMap = new Map<string, ITeam>();
  teams.forEach((team: ITeam) => {
    teamsMap.set(team.id, team);
  });

  for (const match of matchs) {
    const matchDetail = detailLive.find((item) => match.id === item.id);
    const status_id = match.status_id;
    const Kick_off_time = match.match_time ?? 0;

    let matchTimePlayer = null;
    let _scores: IScores = {
      p1: [],
      p2: [],
      x1: [],
      pt: [],
      ft: [],
    };

    if (matchDetail) {
      _scores = matchDetail?.score[3] ?? [];
      if (listStatusPlaying.includes(status_id)) {
        matchTimePlayer = getTimeDetailLive(status_id, Kick_off_time, sportType);
      } else if (status_id === statusEnd) {
        const status = determineMatchOutcome(match, match.home_team_id);
        let statusTxt = "";
        if (status === "win") {
          statusTxt = "Thắng";
        } else if (status === "draw") {
          statusTxt = "Hòa";
        } else {
          statusTxt = "Thua";
        }

        matchTimePlayer = {
          text: statusTxt,
          status_h2h: status,
        };
      }
    } else {
      matchTimePlayer = getTimeDetailLive(status_id, Kick_off_time);
    }

    datas.push({
      ...match,
      scores: _scores,
      home_team: teamsMap.get(match.home_team_id) || null,
      away_team: teamsMap.get(match.away_team_id) || null,
      match_time_player: matchTimePlayer,
    });
  }

  return datas;
};

export const getRoundName = (match: IMatch, type: number | undefined) => {
  if (match.stage) {
    if (type === MatchEnum.STAGE_MODE_LEAGUE) {
      return match.round?.round_num && match.round?.round_num != 0
        ? `Vòng ${match.round.round_num}`
        : "";
    } else if (type === MatchEnum.STAGE_MODE_CUP) {
      // return match.stage.name ? match.stage.name.replace("Round", "Vòng") : "";

      const stage = match.stage;
      const round = match.round as IRound;
      const stageTxts = {
        Group: "Bảng",
        Round: "Vòng",
        "Qualifying Round": "Vòng loại",
        "Group Stage": "Bảng",
        "1/8 Final": "Vòng 1/8",
        "Quarter-finals": "Tứ kết",
        "Quarter Final": "Tứ kết",
        "Semi-finals": "Bán kết",
        Semifinal: "Bán kết",
        "Third Place": "Trận tranh hạng 3",
        Final: "Chung kết",
      } as IItemString;

      const arr = [];
      let stgName: string = stage.name_vi ? stage.name_vi : stage.name;
      if (stageTxts[stgName]) {
        stgName = stageTxts[stgName];
      } else {
        stgName = stgName.replace("Round", "Vòng");
      }
      arr.push(stgName);

      const groups = ["A", "B", "C", "D", "E", "F", "G", "H", "I", "J"];
      if (round["group_num"] && round["group_num"] != 0) {
        arr.push(groups[round["group_num"] - 1]);
      }

      const roundNum = round["round_num"];
      if (roundNum && roundNum != 0) {
        arr.push(`- Vòng ${roundNum}`);
      }

      return arr.join(" ");
    }
  }

  return "";
};

export const getTeamName = (teams: ITeam[], teamId: string) => {
  const team: any = teams.find((team: ITeam) => team.id === teamId);

  if (team) {
    if (team.short_name_vi) {
      return team.short_name_vi;
    } else if (team.short_name) {
      return team.short_name;
    } else {
      return team.name;
    }
  }

  return "";
};

export const getCompetitionName = (
  competitions: ILeague[],
  competitionId: string
) => {
  const competition: any = competitions.find(
    (competition: ILeague) => competition.id === competitionId
  );

  if (competition) {
    if (competition.short_name_vi) {
      return competition.short_name_vi;
    } else if (competition.short_name) {
      return competition.short_name;
    } else {
      return competition.name;
    }
  }

  return "";
};

export const addIncidentToPlayer = (
  players: IPlayer[],
  incidents: IIncident[]
) => {
  let i = 0;
  for (const player of players) {
    // Tìm toàn bộ incidents của player
    const incidentPlayers = incidents.filter((item: IIncident) => {
      if (
        (item.player && item.player.id === player.id) ||
        (item.playerIn && item.playerIn.id === player.id) ||
        (item.playerOut && item.playerOut.id === player.id)
      ) {
        return true;
      }

      return false;
    });
    if (incidentPlayers.length) {
      // gán incident vào player
      for (const incident of incidentPlayers) {
        const check = player.incidents.find(
          (inci: IIncident) => inci.time == incident.time
        );
        if (!check) {
          if (incident.playerIn) {
            incident.in_player = incident.playerIn;
          }
          if (incident.playerOut) {
            incident.out_player = incident.playerOut;
          }
          players[i].incidents.push(incident);
        }
      }
    }
    i++;
  }

  return players;
};

const calculatePercentage = (value: number, total: number): string => {
  if (total === 0) return "0%";
  return `${((value / total) * 100).toFixed(2)}%`;
};

const chooseValue = (
  statistics: IStatistics | any,
  key: string,
  index: number
) => {
  let teamData = index === 1 ? "homeData" : "awayData";
  let data: number | string = 0;

  let _first_serve = `${statistics[teamData].first_serve_success}/${statistics[teamData].first_serve_total}`;
  let _second_serve = `${statistics[teamData].second_serve_success}/${statistics[teamData].second_serve_total}`;
  let _first_serve_points = `${statistics[teamData].first_serve_points_success}/${statistics[teamData].first_serve_success}`;
  let _second_serve_points = `${statistics[teamData].second_serve_points_success}/${statistics[teamData].second_serve_total}`;

  switch (key) {
    case "aces":
      data = statistics[teamData].aces;
      break;
    case "double_faults":
      data = statistics[teamData].double_faults;
      break;
    case "first_serve":
      data = _first_serve;
      break;
    case "first_serve_rate":
      // let [success, total] = _first_serve.split('/').map(Number);
      // data = Number(((success / total) * 100).toFixed(1));
      data = Number((statistics[teamData].first_serve_rate * 100).toFixed(1));
      break;
    case "second_serve":
      data = _second_serve;
      break;
    case "second_serve_rate":
      // let [second_serve_success, second_serve_total] = _second_serve.split('/').map(Number);
      // data = Number(((second_serve_success / second_serve_total) * 100).toFixed(1));
      data = Number((statistics[teamData].second_serve_rate * 100).toFixed(1));
      break;
    case "break_points":
      data = `${statistics[teamData].break_points_success}/${statistics[teamData].break_points_total}`;
      break;
    case "break_points_percent":
      data = Number((statistics[teamData].break_points_rate * 100).toFixed(1));
      break;
    case "total_points":
      data = statistics[teamData].points_won;
      break;
    case "first_serve_points":
      data = _first_serve_points;
      break;
    case "first_serve_points_rate":
      // let [_first_serve_points_success, _first_serve_points_total] = _first_serve_points.split('/').map(Number);
      // data = Number(((_first_serve_points_success / _first_serve_points_total) * 100).toFixed(1));
      data = Number(
        (statistics[teamData].first_serve_points_rate * 100).toFixed(1)
      );
      break;
    case "second_serve_points":
      data = _second_serve_points;
      break;
    case "second_serve_points_rate":
      // let [_second_serve_points_rate_success, _second_serve_points_rate_total] = _first_serve_points.split('/').map(Number);
      // data = Number(((_second_serve_points_rate_success / _second_serve_points_rate_total) * 100).toFixed(1));
      data = Number(
        (statistics[teamData].second_serve_points_rate * 100).toFixed(1)
      );
      break;
  }

  return data;
};

export const getProcessedStats = (
  match: IMatch,
  statistics: IStatistics,
  isDefault: boolean
) => {
  const techKind: Record<string, string> = StatsEnum.techKind;
  return Object.keys(techKind).map((key) => {
    //
    // let _showHomePre: string | number = 0;
    // let _showAwayPre: string | number = 0;
    // let _winHome = '';
    // let _winAway = '';
    // if(key !== 'break_points_percent' &&  StatsEnum.percentKeys.includes(key)){
    //   _showHomePre = chooseValue(statistics, key, 1);
    //   _showAwayPre = chooseValue(statistics, key, 2);
    //   console.log(`KEY: ${key} ${_showHomePre} - ${_showAwayPre}`)
    // }
    return {
      key,
      type: techKind[key],
      home: chooseValue(statistics, key, 1),
      away: chooseValue(statistics, key, 2),

      showHomePre: calculatePercentage(
        Number(chooseValue(statistics, key, 1)),
        Number(chooseValue(statistics, key, 1)) +
          Number(chooseValue(statistics, key, 2))
      ),

      showAwayPre: calculatePercentage(
        Number(chooseValue(statistics, key, 2)),
        Number(chooseValue(statistics, key, 1)) +
          Number(chooseValue(statistics, key, 2))
      ),

      winHome:
        Number(chooseValue(statistics, key, 1)) >
        Number(chooseValue(statistics, key, 2))
          ? "as-statistics__home-win"
          : "",
      winAway:
        Number(chooseValue(statistics, key, 2)) >
        Number(chooseValue(statistics, key, 1))
          ? "as-statistics__away-win"
          : "",
    };
  });
};

const indexPoint = (match_status: number | string) => {
  const pointsMap: { [key: number]: number } = {
    2: 0,
    4: 1,
    6: 2,
    8: 3,
  };
  return pointsMap[match_status as keyof typeof pointsMap] ?? 10;
};

export const initializeData = () => ({
  id: "",
  matchStatus: 0,
  score: { home: 0, away: 0 },
  homeData: {
    aces: 0,
    first_serve_success: 0,
    first_serve_total: 0,
    first_serve_rate: 0,
    second_serve_success: 0,
    second_serve_total: 0,
    second_serve_rate: 0,
    break_points_success: 0,
    break_points_total: 0,
    break_points_rate: 0,
    double_faults: 0,
    unforced_err: 0,
    points_won: 0,
    first_serve_points_success: 0,
    first_serve_points_rate: 0,
    second_serve_points_success: 0,
    second_serve_points_rate: 0,
    max_points_in_a_row: 0,
    receiver_points_won: 0,

    first_serve: 0,
    second_serve: 0,
    break_points: 0,
    break_points_percent: 0,
    total_points: 0,
    first_serve_points: 0,
    second_serve_points: 0,
  },
  awayData: {
    aces: 0,
    first_serve_success: 0,
    first_serve_total: 0,
    first_serve_rate: 0,
    second_serve_success: 0,
    second_serve_total: 0,
    second_serve_rate: 0,
    break_points_success: 0,
    break_points_total: 0,
    break_points_rate: 0,
    double_faults: 0,
    unforced_err: 0,
    points_won: 0,
    first_serve_points_success: 0,
    first_serve_points_rate: 0,
    second_serve_points_success: 0,
    second_serve_points_rate: 0,
    max_points_in_a_row: 0,
    receiver_points_won: 0,

    first_serve: 0,
    second_serve: 0,
    break_points: 0,
    break_points_percent: 0,
    total_points: 0,
    first_serve_points: 0,
    second_serve_points: 0,
  },
});

const getTechnicalStatistics = (data: Array<any>, code: number) => {
  if (data && data[1]) {
    const result = data[1].find((item: any) => item[0] === code);
    return result || [code, 0, 0];
  }

  return [code, 0, 0];
};

const mapMatchDataBK = (match: any, key: number) => {
  console.log("match: ", match);
  let _matchStatus = match.score[1] ?? 0;
  let _match = null;
  switch (_matchStatus) {
    case MatchTennisEnum.STATUS_FIRST_SET:
      _match = match.stats[1] ?? null;
      break;
    case MatchTennisEnum.STATUS_SECOND_SET:
      _match = match.stats[2] ?? null;
      break;
    case MatchTennisEnum.STATUS_THIRD_SET:
      _match = match.stats[3] ?? null;
      break;
    case MatchTennisEnum.STATUS_FOURTH_SET:
      _match = match.stats[5] ?? null;
      break;
    case MatchTennisEnum.STATUS_FIFTH_SET:
      _match = match.stats[5] ?? null;
      break;
    case MatchTennisEnum.STATUS_END:
      _match = match.stats[0] ?? null;
      break;
  }

  console.log("_matchStatus: ", _matchStatus);
  console.log("_match: ", _match);
  console.log("match.stats: ", match.stats);

  return {
    id: match.id,
    matchStatus: _matchStatus,
    score: {
      home: 0,
      away: 0,
    },
    stats: _match,
    homeData: {
      aces: getTechnicalStatistics(_match, 301)[1],
      first_serve_success: getTechnicalStatistics(_match, 302)[1],
      first_serve_total: getTechnicalStatistics(_match, 303)[1],
      first_serve_rate: getTechnicalStatistics(_match, 304)[1],
      second_serve_success: getTechnicalStatistics(_match, 305)[1],
      second_serve_total: getTechnicalStatistics(_match, 306)[1],
      second_serve_rate: getTechnicalStatistics(_match, 307)[1],
      break_points_success: getTechnicalStatistics(_match, 308)[1],
      break_points_total: getTechnicalStatistics(_match, 309)[1],
      break_points_rate: getTechnicalStatistics(_match, 310)[1],
      double_faults: getTechnicalStatistics(_match, 311)[1],
      unforced_err: getTechnicalStatistics(_match, 312)[1],
      points_won: getTechnicalStatistics(_match, 313)[1],
      first_serve_points_success: getTechnicalStatistics(_match, 314)[1],
      first_serve_points_rate: getTechnicalStatistics(_match, 315)[1],
      second_serve_points_success: getTechnicalStatistics(_match, 316)[1],
      second_serve_points_rate: getTechnicalStatistics(_match, 317)[1],
      max_points_in_a_row: getTechnicalStatistics(_match, 318)[1],
      receiver_points_won: getTechnicalStatistics(_match, 319)[1],
    },

    awayData: {
      aces: getTechnicalStatistics(_match, 301)[2],
      first_serve_success: getTechnicalStatistics(_match, 302)[2],
      first_serve_total: getTechnicalStatistics(_match, 303)[2],
      first_serve_rate: getTechnicalStatistics(_match, 304)[2],
      second_serve_success: getTechnicalStatistics(_match, 305)[2],
      second_serve_total: getTechnicalStatistics(_match, 306)[2],
      second_serve_rate: getTechnicalStatistics(_match, 307)[2],
      break_points_success: getTechnicalStatistics(_match, 308)[2],
      break_points_total: getTechnicalStatistics(_match, 309)[2],
      break_points_rate: getTechnicalStatistics(_match, 310)[2],
      double_faults: getTechnicalStatistics(_match, 311)[2],
      unforced_err: getTechnicalStatistics(_match, 312)[2],
      points_won: getTechnicalStatistics(_match, 313)[2],
      first_serve_points_success: getTechnicalStatistics(_match, 314)[2],
      first_serve_points_rate: getTechnicalStatistics(_match, 315)[2],
      second_serve_points_success: getTechnicalStatistics(_match, 316)[2],
      second_serve_points_rate: getTechnicalStatistics(_match, 317)[2],
      max_points_in_a_row: getTechnicalStatistics(_match, 318)[2],
      receiver_points_won: getTechnicalStatistics(_match, 319)[2],
    },
  };
};

const sortedData = (data: IStatsJsonData[]) => {
  const hasDone = data.some(
    (item) => item.matchStatus === MatchTennisEnum.STATUS_END
  );

  return data.sort((a: IStatsJsonData, b: IStatsJsonData) => {
    const matchStatusA =
      typeof a.matchStatus === "number" ? a.matchStatus : Number(a.matchStatus);
    const matchStatusB =
      typeof b.matchStatus === "number" ? b.matchStatus : Number(b.matchStatus);

    if (hasDone) {
      if (matchStatusA === MatchTennisEnum.STATUS_END) return -1;
      if (matchStatusB === MatchTennisEnum.STATUS_END) return 1;
    }

    // Sắp xếp theo matchStatus tăng dần
    return matchStatusA - matchStatusB;
  });
};

const mapMatchData = (
  match_id: string | number,
  stats: any,
  statsLast: Array<any>
) => {
  let _matchStatus = stats[0] ?? 0;
  let _status_id = 0;
  let _match = null;
  switch (_matchStatus) {
    case 1:
      _match = statsLast.find((item) => item[0] === 1) || null;
      _status_id = MatchTennisEnum.STATUS_FIRST_SET;
      break;
    case 2:
      _match = statsLast.find((item) => item[0] === 2) || null;
      _status_id = MatchTennisEnum.STATUS_SECOND_SET;
      break;
    case 3:
      _match = statsLast.find((item) => item[0] === 3) || null;
      _status_id = MatchTennisEnum.STATUS_THIRD_SET;
      break;
    case 4:
      _match = statsLast.find((item) => item[0] === 4) || null;
      _status_id = MatchTennisEnum.STATUS_FOURTH_SET;
      break;
    case 5:
      _match = statsLast.find((item) => item[0] === 5) || null;
      _status_id = MatchTennisEnum.STATUS_FIFTH_SET;
      break;
    case 0:
      _match = statsLast.find((item) => item[0] === 0) || null;
      _status_id = MatchTennisEnum.STATUS_END;
      break;
  }

  return {
    id: match_id,
    matchStatus: _status_id,
    score: {
      home: 0,
      away: 0,
    },
    stats: _match,
    homeData: {
      aces: getTechnicalStatistics(_match, 301)[1],
      first_serve_success: getTechnicalStatistics(_match, 302)[1],
      first_serve_total: getTechnicalStatistics(_match, 303)[1],
      first_serve_rate: getTechnicalStatistics(_match, 304)[1],
      second_serve_success: getTechnicalStatistics(_match, 305)[1],
      second_serve_total: getTechnicalStatistics(_match, 306)[1],
      second_serve_rate: getTechnicalStatistics(_match, 307)[1],
      break_points_success: getTechnicalStatistics(_match, 308)[1],
      break_points_total: getTechnicalStatistics(_match, 309)[1],
      break_points_rate: getTechnicalStatistics(_match, 310)[1],
      double_faults: getTechnicalStatistics(_match, 311)[1],
      unforced_err: getTechnicalStatistics(_match, 312)[1],
      points_won: getTechnicalStatistics(_match, 313)[1],
      first_serve_points_success: getTechnicalStatistics(_match, 314)[1],
      first_serve_points_rate: getTechnicalStatistics(_match, 315)[1],
      second_serve_points_success: getTechnicalStatistics(_match, 316)[1],
      second_serve_points_rate: getTechnicalStatistics(_match, 317)[1],
      max_points_in_a_row: getTechnicalStatistics(_match, 318)[1],
      receiver_points_won: getTechnicalStatistics(_match, 319)[1],
    },

    awayData: {
      aces: getTechnicalStatistics(_match, 301)[2],
      first_serve_success: getTechnicalStatistics(_match, 302)[2],
      first_serve_total: getTechnicalStatistics(_match, 303)[2],
      first_serve_rate: getTechnicalStatistics(_match, 304)[2],
      second_serve_success: getTechnicalStatistics(_match, 305)[2],
      second_serve_total: getTechnicalStatistics(_match, 306)[2],
      second_serve_rate: getTechnicalStatistics(_match, 307)[2],
      break_points_success: getTechnicalStatistics(_match, 308)[2],
      break_points_total: getTechnicalStatistics(_match, 309)[2],
      break_points_rate: getTechnicalStatistics(_match, 310)[2],
      double_faults: getTechnicalStatistics(_match, 311)[2],
      unforced_err: getTechnicalStatistics(_match, 312)[2],
      points_won: getTechnicalStatistics(_match, 313)[2],
      first_serve_points_success: getTechnicalStatistics(_match, 314)[2],
      first_serve_points_rate: getTechnicalStatistics(_match, 315)[2],
      second_serve_points_success: getTechnicalStatistics(_match, 316)[2],
      second_serve_points_rate: getTechnicalStatistics(_match, 317)[2],
      max_points_in_a_row: getTechnicalStatistics(_match, 318)[2],
      receiver_points_won: getTechnicalStatistics(_match, 319)[2],
    },
  };
};

export const convertData = (
  data: IStatsJsonData[],
  match_id: string | number
): any => {
  if (data.length) {
    const statsLast = data[data.length - 1].stats;
    let convertResponseData = statsLast.map((stats, key) =>
      mapMatchData(match_id, stats, statsLast)
    );
    return convertResponseData ? sortedData(convertResponseData) : [];
  }
  return [];
};

export const mapPlayer = (playerId: string, listPlayer: IPlayer[]) => {
  if (!listPlayer) {
    return null;
  }
  return listPlayer.find((player) => player.id === playerId);
};

const groupShootingPointsByPartOfGame = (
  stp: IShootingPoints | undefined | any
) => {
  if (!stp?.results) {
    return null;
  }
  const groupPartOfGame = stp.results.reduce((acc: any, item: any) => {
    if (!acc[item[7]]) {
      // 7 is part of game (bar, quarter, half)
      acc[item[7]] = [];
    }
    acc[item[7]].push(item);
    return acc;
  }, {});
  return groupPartOfGame;
};

const reverseArray = (data: any): Array<any> => {
  const t = [];
  for (const key in data) {
    const s = data[key].reverse();
    t.push(...s);
  }
  return t;
};

const calculateResponseSums = (jsonData: Array<any>) => {
  return jsonData.map((item, i) => {
    let sumAway = 0;
    let sumHome = 0;
    for (let j = 0; j <= i; j++) {
      if (jsonData[j][0] === 1) {
        sumHome += jsonData[j][4];
      }
      if (jsonData[j][0] === 2) {
        sumAway += jsonData[j][4];
      }
    }
    return [...item, sumHome, sumAway];
  });
};

export const convertShootingPointsData = (
  data: IShootingPointResponseData
): any => {
  const { shootingPoints } = data;
  if (!shootingPoints) {
    return null;
  }
  const groupShootingPoints = groupShootingPointsByPartOfGame(shootingPoints);
  const arrReversed = reverseArray(groupShootingPoints);
  const sumMatch = calculateResponseSums(arrReversed);

  const result = groupShootingPointsByPartOfGame({ results: sumMatch });
  return result;
};

const calculateShootingPointsOfPlayer = (
  objectData: any[]
): IPlayerDataShooting[] => {
  const result = [];
  for (const property in objectData) {
    const value = objectData[property];
    let totalPont = 0;
    value.map((item: Array<any>) => {
      let point = item[4];
      totalPont += point;
    });
    result.push({
      id: property,
      teamId: value[0][0],
      scoreOfSorting: value[0][4],
      x: value[0][5],
      y: value[0][6],
      numberOfBar: value[0][7],
      time: value[0][8],
      totalPoint: totalPont,
    });
  }
  return result;
};

export const groupByPlayer = (data: IShootingPoints | null | undefined) => {
  if (!data) {
    return null;
  }
  const dataGroupByPlayer = data?.results.reduce((acc: any, item: any) => {
    if (!acc[item[1]]) {
      acc[item[1]] = [];
    }
    acc[item[1]].push(item);
    return acc;
  }, {});
  const jsonShootingPoints = calculateShootingPointsOfPlayer(
    dataGroupByPlayer
  ).sort((a, b) => ((b.totalPoint || 0) - (a.totalPoint || 0) >= 0 ? 1 : -1));
  return jsonShootingPoints;
};

import type {
  ITournamentRound,
  ITournamentMatchUp,
  ITournamentTeam,
  ITournamentJsonData,
} from "~src/interfaces/tennis";

export const getMappingRounds = (
  rounds: ITournamentRound[],
  matchUps: ITournamentMatchUp[],
  teams: ITournamentTeam[]
) => {
  const populatedRounds = rounds.map((round) => {
    const filteredMatchUps = matchUps
      .filter((matchUp) => matchUp.round_id === round.id)
      .map((matchUp) => {
        const homeTeam =
          teams.find((team) => team.id === matchUp.home_team_id) || {};
        const awayTeam =
          teams.find((team) => team.id === matchUp.away_team_id) || {};

        return {
          ...matchUp,
          home_team: homeTeam,
          away_team: awayTeam,
        };
      });

    return {
      ...round,
      match_ups: filteredMatchUps,
      match_up_lines: Math.floor(filteredMatchUps.length / 2),
    };
  });

  return populatedRounds;
};

export const chunkArray = (array: any[], chunkSize: number) => {
  let result = [];
  for (let i = 0; i < array.length; i += chunkSize) {
    result.push(array.slice(i, i + chunkSize));
  }
  return result;
};

export const getMappingGroups = (
  groups: ITournamentGroup[],
  rounds: ITournamentRound[],
  matchUps: ITournamentMatchUp[],
  teams: ITournamentTeam[],
  matches: any[]
) => {
  const chunkArray = (array: any[], chunkSize: number) => {
    let result = [];
    for (let i = 0; i < array.length; i += chunkSize) {
      result.push(array.slice(i, i + chunkSize));
    }
    return result;
  };

  const populatedGroups = groups.map((group) => {
    const filteredRounds = rounds
      .filter((round) => round.group_id === group.id)
      .map((round) => {
        const matchUpsForRound = matchUps.filter(
          (match) => match.round_id === round.id
        );
        const matchUpsWithTeams = matchUpsForRound.map((match) => ({
          ...match,
          home_team: teams.find((team) => team.id === match.home_team_id) || {},
          away_team: teams.find((team) => team.id === match.away_team_id) || {},
          i_match: matches.find((item) => match.match_ids.includes(item.id)) || {}
        }));

        return {
          ...round,
          match_ups: matchUpsWithTeams,
          match_up_lines: Math.floor(matchUpsForRound.length / 2),
          match_up_chunks: chunkArray(matchUpsWithTeams, 2), // Chia thành các mảng con có 2 phần tử mỗi mảng con
        };
      });

    return {
      ...group,
      rounds: filteredRounds,
    };
  });

  return populatedGroups as ITournamentGroup[];
};

export const findGroupByMatchId = (
  groups: ITournamentGroup[],
  matchId: string
) => {
  const groupWithMatch = groups.find(group => 
    group?.rounds?.some(round => 
      round?.match_ups?.some(match => 
        match.match_ids?.includes(matchId)
      )
    )
  ) as ITournamentGroup;

  if (groupWithMatch) return groupWithMatch;

  return groups
    .filter(group => group?.start_time)
    .sort((a, b) => {
      const timeA = new Date(a.start_time).getTime();
      const timeB = new Date(b.start_time).getTime();
      return Math.abs(Date.now() - timeA) - Math.abs(Date.now() - timeB);
    })[0] || null;
};

export const getMappingTimelinesOverViews = (timelines: ITimeline[]) => {
  const res = timelines
    .sort((a, b) => b.set - a.set)
    .map((set) => {
      const roundsWithAbc = set.rounds
        .sort((a, b) => b.round - a.round)
        .map((round) => {
          let homeScore = 0;
          let awayScore = 0;

          round.points.forEach((point) => {
            const homePoint = point[0];
            const awayPoint = point[1];

            if (homePoint === "A" || parseInt(homePoint, 10) >= 40) {
              homeScore++;
            }
            if (awayPoint === "A" || parseInt(awayPoint, 10) >= 40) {
              awayScore++;
            }
          });

          return {
            ...round,
            score_round: {
              home: homeScore,
              away: awayScore,
            },
          };
        });

      // Calculate total scores from rounds
      const totalScores = roundsWithAbc.reduce(
        (acc, round) => {
          acc.home += round.score_round.home;
          acc.away += round.score_round.away;
          return acc;
        },
        { home: 0, away: 0 }
      );

      return {
        ...set,
        rounds: roundsWithAbc,
        scores: totalScores, // Add scores object
      };
    });

  return res;
};

export const getIdsTournaments = (
  tournaments: ITournament[],
  ground_id: number = 0
) => {
  return tournaments
    .filter((item: ITournament) =>
      ground_id ? item?.ground_id === ground_id : true
    )
    .map((item) => item.id);
};

export const mapTeamToH2H = (data: IH2h, sportType: string = "tennis") => {
  const teamsMap = new Map<string, ITeam>();
  const tournamentsMap = new Map<string, ITournament>();
  const countriesMap = new Map<string, ICountry>();

  let _tournaments =
    sportType === "volleyball" || sportType === "baseball"
      ? data?.uniqueTournaments ?? []
      : data.tournaments;

  data.teams.forEach((team) => {
    teamsMap.set(team.id, team);
  });
  _tournaments.forEach((tournament) => {
    tournamentsMap.set(tournament.id, tournament);
  });

  if (data.countries) {
    data.countries.forEach((country) => {
      countriesMap.set(country.id, country);
    });
  }

  const enrichMatches = (matches: IVS[]): IVS[] => {
    return matches.map((match) => ({
      ...match,
      home_team: teamsMap.get(match.home_team_id),
      away_team: teamsMap.get(match.away_team_id),
      tournament: tournamentsMap.get(match.tournament_id),
    }));
  };

  let __vs = sportType === "tennis" ? data.vs : data?.historyVSMatches ?? [];
  let __home =
    sportType === "tennis" ? data.home : data?.historyHomeMatches ?? [];
  let __away =
    sportType === "tennis" ? data.away : data?.historyAwayMatches ?? [];

  data.home = enrichMatches(__home);
  data.away = enrichMatches(__away);
  data.vs = enrichMatches(__vs);
  data.future.home = enrichMatches(data.future.home);
  data.future.away = enrichMatches(data.future.away);

  data.teams = data.teams.map((team) => ({
    ...team,
    country: team.country_id ? countriesMap.get(team.country_id) : undefined,
  }));
  return data;
};

export const mapTeamToMatchOther = (matches: IMatch[], teams: ITeam[]) => {
  const teamsMap = new Map<string, ITeam>();

  teams.forEach((team: ITeam) => {
    teamsMap.set(team.id, team);
  });
  return matches.map((match) => ({
    ...match,
    home_team: teamsMap.get(match.home_team_id) || null,
    away_team: teamsMap.get(match.away_team_id) || null,
  }));
};

export const getScoreForSet = (
  score: IScoreStatistics,
  set: number = 1
): string | undefined => {
  if (score && score[3]) {
    const scoreDynamic = score[3];
    const key = `p${set}` as keyof IScoreDynamic;
    const __score = scoreDynamic[key];
    if (__score) {
      return `, ${__score.join(" - ")}`;
    }
  }

  return "";
};

export const setActiveScoreBracketItem = (
  home_team_id: number,
  away_team_id: number,
  mode: string = "home"
) => {
  if (mode == "home") {
    return home_team_id > away_team_id;
  } else {
    return away_team_id > home_team_id;
  }
};

export const getLastStatistic = (statistics: IStatistics[]) => {
  if (statistics) {
    return;
  }

  return null;
};
